import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Download, Shield, User, Bot, Wrench, BookOpen } from 'lucide-react';

const EVENT_ICONS = {
  'user.message': User,
  'user.attachment': User,
  'agent.message': Bot,
  'agent.thinking': Bot,
  'agent.decision': Bot,
  'agent.action': Bot,
  'agent.tool_call': Wrench,
  'agent.memory_read': BookOpen,
  'agent.memory_write': BookOpen,
  'human.approval_requested': Shield,
  'human.approval_granted': Shield,
  'human.approval_denied': Shield,
};

const EVENT_COLOR = {
  'user.message': '#3EF1C7',
  'agent.message': '#FFD93D',
  'agent.thinking': '#9B59B6',
  'agent.decision': '#3498DB',
  'agent.tool_call': '#FFA500',
  'agent.memory_read': '#94A3B8',
  'human.approval_requested': '#FF6B6B',
  'human.approval_granted': '#10B981',
};

export default function TranscriptViewerPage() {
  const { transcriptId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!transcriptId) return;
    fetch(`/api/v2/transcripts/${transcriptId}/replay`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [transcriptId]);

  const exportTranscript = async (format) => {
    const r = await fetch(`/api/v2/transcripts/${transcriptId}/export?format=${format}&purpose=user_view`, {
      method: 'POST',
    });
    const d = await r.json();
    if (format === 'json' && d.payload) {
      const blob = new Blob([JSON.stringify(d.payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript_${transcriptId}.json`;
      a.click();
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0F1C] text-[#94A3B8] p-12">Loading transcript...</div>;
  if (!data || data.error) return <div className="min-h-screen bg-[#0A0F1C] text-[#FF6B6B] p-12">Transcript not found</div>;

  const { transcript, events } = data;

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/transcripts" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All transcripts
          </Link>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">Conversation Audit</span>
          </div>
          <button onClick={() => exportTranscript('json')} className="text-sm flex items-center gap-2 px-3 py-1 border border-[#22304A] rounded hover:bg-[#11192A]">
            <Download className="w-4 h-4" /> Export JSON
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-[#64748B]">Channel</div>
              <div className="text-white">{transcript.channel}</div>
            </div>
            <div>
              <div className="text-xs text-[#64748B]">Events</div>
              <div className="text-white">{transcript.event_count}</div>
            </div>
            <div>
              <div className="text-xs text-[#64748B]">Decisions</div>
              <div className="text-white">{(transcript.decisions_proposed || []).length}</div>
            </div>
            <div>
              <div className="text-xs text-[#64748B]">Cost (credits)</div>
              <div className="text-white">{transcript.total_cost_credits?.toFixed(2) || '0.00'}</div>
            </div>
          </div>
          {transcript.transcript_root && (
            <div className="mt-4 pt-4 border-t border-[#22304A]">
              <div className="text-xs text-[#64748B] mb-1">Merkle root (tamper-evidence)</div>
              <div className="text-xs font-mono text-[#3EF1C7] break-all">{transcript.transcript_root}</div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {(events || []).map(e => {
            const Icon = EVENT_ICONS[e.event_type] || Bot;
            const color = EVENT_COLOR[e.event_type] || '#94A3B8';
            return (
              <div key={e.event_id} className="bg-[#0D1628] border border-[#22304A] rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${color}20` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-mono text-[#64748B]">{e.event_type}</div>
                      <div className="text-xs text-[#64748B]">#{e.sequence} · {e.actor}</div>
                    </div>
                    {e.content && (
                      <div className="text-sm text-white whitespace-pre-wrap break-words">{e.content}</div>
                    )}
                    {e.references && e.references.length > 0 && (
                      <div className="mt-2 flex gap-2 text-xs">
                        {e.references.map((r, i) => (
                          <span key={i} className="text-[#64748B]">
                            → {r.collection}:{r.record_id?.slice(0, 12)}...
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
